// vzhlad_predajne_v2 (20. 10. 2026, ústava 61.10): starý oranžový panel/akcent nahradený hodnotami témy — 1 miest
// BlockyMobile.jsx — Matrix v2 mobilný modul Bločky
// QR scanner DOSTUPNÝ Z PRVÉHO OKNA (Mirko 2026-05-25 — ústava sek. 60.5)
// Tok: QR scan → ekasa-lookup → AddBlocek s prefill → save (s duplicate handling)
// CDN: html5-qrcode@2.3.8 (lazy load pri kliku na Skenovať QR)
import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react'
import { useDirtyFlag } from '../hooks/useDirtyState.js'   // straz_v4 (29. 9. 2026): rozpísaný formulár drží spoločnú stráž odchodu (ústava 53.1.b)
import { COLORS, TYPO, SPACE, RADIUS, SK, todayLocal, hladaj } from '../theme.js'
import { fetchAuth } from '../auth/fetchAuth.js'
import { useToast } from '../components/Toast.jsx'

const COST_CATS = {
  // ZJEDNOTENÉ s desktopom (FakturyView REZIA_CATS/ZAKAZKA_CATS) — ústava 25.3, Mirko 2026-06-06
  rezia: [
    'Mzdy a odmeny', 'Odvody / Sociálna poisťovňa', 'PHM (pohonné hmoty)', 'Poistenie',
    'Prenájom priestorov', 'Leasing vozidla', 'Opravy áut / servis', 'Splátka úveru',
    'Bankové poplatky', 'Telefón / Internet', 'Účtovníctvo / Právne služby', 'IT a technika',
    'Kancelárske potreby', 'Reštaurácie / Stravovanie', 'Nákup náradia / zariadenia',
    'Oprava náradia / servis', 'Prenájom náradia', 'Notár / overenie / poplatky', 'Iné réžijné náklady',
  ],
  zakazka: [
    'Stavebný materiál', 'Zriadenie staveniska', 'Mzdy a odmeny', 'PHM (pohonné hmoty — zákazka)',
    'Nákup náradia / zariadenia', 'Oprava náradia / servis', 'Prenájom náradia',
    'Subdodávka práce', 'Doprava na zákazku', 'Prenájom strojov', 'Iný zákazkový výdavok',
  ],
}

// ── Lazy load html5-qrcode CDN (zo starého mview) ──
function ensureQrLib() {
  return new Promise((resolve, reject) => {
    if (window.Html5Qrcode) return resolve()
    if (document.getElementById('html5-qrcode-script')) {
      const t = setInterval(() => {
        if (window.Html5Qrcode) { clearInterval(t); resolve() }
      }, 100)
      setTimeout(() => { clearInterval(t); window.Html5Qrcode ? resolve() : reject(new Error('Skener sa nenačítal')) }, 8000)
      return
    }
    const s = document.createElement('script')
    s.id = 'html5-qrcode-script'
    s.src = 'https://unpkg.com/html5-qrcode@2.3.8/html5-qrcode.min.js'
    s.onload = () => resolve()
    s.onerror = () => reject(new Error('Sieť — nepodarilo sa stiahnuť skener'))
    document.head.appendChild(s)
  })
}

export default function BlockyMobile() {
  const [items, setItems] = useState([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [scanMode, setScanMode] = useState(false)        // overlay so scannerom
  const [scanError, setScanError] = useState('')
  const [scanBusy, setScanBusy] = useState(false)
  const [scannedRaw, setScannedRaw] = useState('')       // debug: posledný naskenovaný text
  const [prefillForm, setPrefillForm] = useState(null)   // ak !=null → AddBlocek je v predvyplnenom móde
  const [prefillItems, setPrefillItems] = useState([])   // eKasa items
  const scannerRef = useRef(null)
  const toast = useToast()
  const [kto, setKto] = useState(null)   // BLOCKY_BANKA_V3: kto smie bloček pridať (server, ten istý register ako web a appky)
  useEffect(() => { fetchAuth('/api/incoming-invoices?kto=1').then(r => r.ok ? r.json() : null).then(d => { if (d) setKto(d) }).catch(() => {}) }, [])

  function load() {
    setLoading(true)
    fetchAuth('/api/incoming-invoices?doc_type=blocok')
      .then(r => r.ok ? r.json() : [])
      .then(d => setItems((Array.isArray(d) ? d : []).filter(i => i.doc_type === 'blocok' && !i._cudzi)))   // BLOCKY_BANKA_V3: len vlastné (cudzí bloček je len riadok pre súčty)
      .finally(() => setLoading(false))
  }
  useEffect(load, [])

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    let list = items.filter(i => i.doc_type === 'blocok')
    if (q) list = list.filter(i => hladaj(`${i.supplier_name || ''} ${i.cost_category || ''} ${i.notes || ''}`, q))
    return list.sort((a, b) => (b.date_issued || '').localeCompare(a.date_issued || ''))
  }, [items, search])

  const kpi = useMemo(() => {
    const monthPrefix = new Date().toISOString().slice(0, 7)
    const thisMonth = items.filter(i => (i.date_issued || '').startsWith(monthPrefix))
    return {
      count: items.length,
      thisMonth: thisMonth.length,
      thisMonthSum: thisMonth.reduce((a, r) => a + Number(r.amount_total || 0), 0),
    }
  }, [items])

  // ── QR HANDLERS ──
  const stopScanner = useCallback(async () => {
    if (scannerRef.current) {
      try { await scannerRef.current.stop() } catch (_) {}
      try { scannerRef.current.clear() } catch (_) {}
      scannerRef.current = null
    }
  }, [])

  const handleCode = useCallback(async (qrText) => {
    const trimmed = String(qrText).trim()
    setScannedRaw(trimmed)
    console.log('[BlockyMobile] QR scanned:', trimmed)
    await stopScanner()
    setScanBusy(true); setScanError('')
    try {
      const r = await fetchAuth('/api/incoming-invoices/ekasa-lookup', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ qrText: trimmed }),
      })
      const d = await r.json()
      console.log('[BlockyMobile] ekasa-lookup response:', r.status, d)
      if (!r.ok) {
        setScanError(`eKasa: ${d.error || 'nenašlo bloček'} (HTTP ${r.status}). Skús zadať ručne alebo „Pokračovať aj tak".`)
        return
      }
      const x = d.extracted || {}
      // ── Duplicate check pred otvorením formulára ──
      const dupKey = d.receiptId || x.invoice_number || ''
      if (dupKey) {
        const dup = items.find(it =>
          (it.invoice_number || '').includes(dupKey) ||
          (it.notes || '').includes(dupKey)
        )
        if (dup) {
          toast.error(`Tento bloček už máš! ${dup.date_issued || ''} · ${dup.supplier_name || ''} · ${SK.eur(dup.amount_total || 0)}`)
          setScanMode(false)
          return
        }
      }
      // ── prefill AddBlocek (so všetkými náležitosťami z eKasa) ──
      setPrefillForm({
        supplier_name: x.supplier_name || '',
        supplier_ico:  x.supplier_ico  || '',
        supplier_dic:  x.supplier_dic  || '',
        date_issued:   x.date_issued   || todayLocal(),
        amount_excl_vat: x.amount_excl_vat != null ? Number(x.amount_excl_vat) : null,
        amount_total:  x.amount_total != null ? String(x.amount_total) : '',
        vat_rate:      x.vat_rate     != null ? x.vat_rate : 23,
        vat_lines:     Array.isArray(x.vat_lines) ? x.vat_lines : [],  // [{rate, base, amount}]
        cost_center:   'rezia',
        cost_category: '',
        project_id:    '',
        payment_method: 'karta',   // V3 P1 (ústava 12.6): pri skene voľba karta alebo hotovosť, predvolená karta („90 % karta“)
        invoice_number: d.receiptId || '',
        ekasa_id: x.ekasa_id || d.receiptId || '',   // ústava 135.3 — jediný kľúč duplicity bločku
        notes: `eKasa: ${d.receiptId || dupKey}`,
      })
      setPrefillItems(Array.isArray(d.items) ? d.items : [])
      setScanMode(false)
    } catch (e) {
      setScanError('Chyba: ' + e.message)
    } finally {
      setScanBusy(false)
    }
  }, [stopScanner, items, toast])

  // DOSLOVA 1:1 ako starý mview (MobileView.jsx L1172-1185) — fungovalo
  const startScan = useCallback(async () => {
    setScanError('')
    setScanMode(true)
    try {
      await ensureQrLib()
      await new Promise(r => setTimeout(r, 100))
      if (scannerRef.current) { try { await scannerRef.current.stop() } catch(_){} scannerRef.current = null }
      const scanner = new window.Html5Qrcode('qr-reader')
      scannerRef.current = scanner
      await scanner.start(
        { facingMode: 'environment' },
        { fps: 15, qrbox: { width: 280, height: 280 } },
        (text) => handleCode(text),
        () => {},
      )
    } catch (e) {
      setScanError('Skener: ' + (e.message || 'kamera nedostupná'))
    }
  }, [handleCode])

  // ── Foto QR fallback (robustnejšie ako live stream) ──
  const photoInputRef = useRef(null)
  const aiPhotoRef = useRef(null)
  const uidInputRef = useRef(null)

  // AI foto celého bločku → Claude Vision extract → prefill → uloží do Bločky (incoming_invoices)
  const handleAiPhoto = useCallback(async (e) => {
    const file = e.target.files && e.target.files[0]
    if (!file) return
    try { e.target.value = '' } catch (_) {}
    setScanError(''); setScanBusy(true); setScanMode(true)
    toast.success('AI číta bloček…')
    try {
      const fd = new FormData(); fd.append('file', file); fd.append('is_receipt', 'true')
      const r = await fetchAuth('/api/incoming-invoices/extract', { method: 'POST', body: fd })
      const _resp = await r.json()
      if (!r.ok) { setScanError('AI: ' + (_resp.error || ('HTTP ' + r.status))); return }
      const d = _resp.extracted || _resp   // backend vracia { extracted: {...} }
      const aiFail = !!(_resp.noKey || _resp.ai_error)   // V3 P1: AI neprečítala — fotka je aj tak priložená, vyplní sa ručne
      setPrefillForm({
        supplier_name: d.supplier_name || '', supplier_ico: d.supplier_ico || '', supplier_dic: d.supplier_dic || '',
        date_issued: d.date_issued || todayLocal(),
        amount_excl_vat: d.amount_ex_vat != null ? Number(d.amount_ex_vat) : null,
        amount_total: d.amount_total != null ? String(d.amount_total) : '',
        vat_rate: d.vat_rate != null ? d.vat_rate : 23, vat_lines: [],
        cost_center: 'rezia', cost_category: d.category || '', project_id: '',
        payment_method: /hotov|cash/i.test(String(d.payment_method || '')) ? 'hotovost' : 'karta', invoice_number: d.invoice_number || '',
        notes: d.notes || 'AI foto bloček',
        doc_file: _resp.doc_file || '',   // V3 P1: fotka bločku ostáva ako príloha dokladu
      })
      setPrefillItems([])
      setScanMode(false)
      if (aiFail) toast.error('AI bloček neprečítala' + (_resp.ai_error ? ' (' + _resp.ai_error + ')' : '') + ' — vyplň údaje ručne, fotka je priložená')
      else toast.success('AI vyplnila údaje — skontroluj, zvoľ platbu a ulož')
    } catch (err) { setScanError('Chyba AI: ' + err.message) } finally { setScanBusy(false) }
  }, [toast])

  // manuálne UID dokladu → ekasa-lookup
  const handleUidLookup = useCallback(() => {
    const v = (uidInputRef.current && uidInputRef.current.value || '').trim()
    if (!v) { toast.error('Zadaj UID / kód dokladu'); return }
    handleCode(v)
  }, [handleCode, toast])

  const handlePhotoQr = useCallback(async (e) => {
    const file = e.target.files && e.target.files[0]
    if (!file) return
    try { e.target.value = '' } catch (_) {}
    setScanError(''); setScannedRaw(''); setScanBusy(true); setScanMode(true)
    try {
      await ensureQrLib()
      await new Promise(r => setTimeout(r, 100))
      const decoder = new window.Html5Qrcode('qr-reader')
      try {
        const text = await decoder.scanFile(file, /* showImage */ false)
        await handleCode(text)
      } finally {
        try { decoder.clear() } catch (_) {}
      }
    } catch (err) {
      setScanError('Foto: QR sa nepodarilo prečítať z obrázka. Skús ostrejšie / bližšie.')
      setScanBusy(false)
    }
  }, [handleCode])

  useEffect(() => () => { stopScanner() }, [stopScanner])

  // ── RENDER: prefill mode (AddBlocek s predvyplneným formulárom) ──
  if (prefillForm) {
    return <AddBlocekMobile
      prefill={prefillForm}
      ekasaItems={prefillItems}
      onClose={() => { setPrefillForm(null); setPrefillItems([]) }}
      onSaved={() => { setPrefillForm(null); setPrefillItems([]); load() }}
    />
  }

  // ── RENDER: scan overlay ──
  if (scanMode) {
    return (
      <div style={{ padding: SPACE.md, display: 'flex', flexDirection: 'column', gap: SPACE.md }}>
        <button onClick={() => { stopScanner(); setScanMode(false); setScanError('') }} style={{
          background: 'transparent', border: 'none', cursor: 'pointer',
          color: COLORS.nav, fontSize: TYPO.sizes.md, fontWeight: TYPO.weights.semibold,
          textAlign: 'left', padding: '8px 0', minHeight: 44,
        }}>‹ Zrušiť skenovanie</button>
        <h2 style={{ margin: 0, fontSize: TYPO.sizes.lg, fontWeight: TYPO.weights.extra }}>Skenuj QR z bločka</h2>
        <div id="qr-reader" style={{ width: '100%', minHeight: 320, borderRadius: RADIUS.md, overflow: 'hidden', background: '#000' }} />
        {scanBusy && <div style={{ color: COLORS.muted, textAlign: 'center' }}>Vyhľadávam v eKasa…</div>}
        {scannedRaw && (
          <div style={{
            padding: SPACE.sm, background: COLORS.bgSoft, borderRadius: RADIUS.md,
            fontSize: TYPO.sizes.xs, color: COLORS.muted, fontFamily: TYPO.mono,
            wordBreak: 'break-all',
          }}>
            <div style={{ fontWeight: TYPO.weights.bold, color: COLORS.text, marginBottom: 4 }}>Naskenovaný kód:</div>
            {scannedRaw}
          </div>
        )}
        {scanError && (
          <>
            <div style={{
              padding: SPACE.md, background: 'rgba(239,68,68,0.08)',
              border: `1px solid ${COLORS.remove}`, borderRadius: RADIUS.md,
              color: COLORS.remove, fontSize: TYPO.sizes.sm,
            }}>{scanError}</div>
            <button onClick={() => {
              setPrefillForm({
                invoice_number: scannedRaw,
                notes: `Manuál po QR: ${scannedRaw}`,
              })
              setScanMode(false); setScanError(''); setScannedRaw('')
            }} style={{
              minHeight: 56, fontSize: TYPO.sizes.md, fontWeight: TYPO.weights.semibold,
              background: COLORS.warn || '#f59e0b', color: '#fff', border: 'none',
              borderRadius: RADIUS.md, cursor: 'pointer', fontFamily: 'inherit',
            }}>Pokračovať aj tak (vyplniť ručne)</button>
            <button onClick={() => { setScanError(''); setScannedRaw(''); startScan() }} style={{
              minHeight: 48, fontSize: TYPO.sizes.md, fontWeight: TYPO.weights.semibold,
              background: 'transparent', color: COLORS.text, border: `1px solid ${COLORS.line}`,
              borderRadius: RADIUS.md, cursor: 'pointer', fontFamily: 'inherit',
            }}>Skús znova</button>
          </>
        )}
      </div>
    )
  }

  // ── RENDER: hlavné okno (zoznam) — QR button JE PRVÝ ──
  return (
    <div style={{ display: 'flex', flexDirection: 'column', padding: SPACE.md, gap: SPACE.md }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: SPACE.md }}>
        <h1 style={{ margin: 0, fontSize: TYPO.sizes.xl, fontWeight: TYPO.weights.extra, flex: 1 }}>
          Bločky
        </h1>
      </div>

      <div style={{
        padding: SPACE.md, background: COLORS.bgSoft,
        border: `1px solid ${COLORS.line}`, borderRadius: RADIUS.md,
      }}>
        <div style={{ fontSize: TYPO.sizes.xs, color: COLORS.muted, marginBottom: 4 }}>Tento mesiac</div>
        <div style={{ fontSize: TYPO.sizes.xl, fontWeight: TYPO.weights.extra, color: COLORS.total }}>
          {kpi.thisMonth} ks · {SK.eur(kpi.thisMonthSum)}
        </div>
      </div>

      {kto && kto.smie_pridat === false ? <div style={{ padding: SPACE.md, background: COLORS.bgSoft, border: `1px solid ${COLORS.line}`, borderRadius: RADIUS.md, fontSize: TYPO.sizes.sm }}>Bločky do Matrixu nahráva vedenie a office. Bloček odovzdaj Lucii — nahrá ho a ak si platil z vlastného, vyplatí ti hotovosť.</div> : <>
      {/* QR scan = primárne tlačidlo (väčšie, vyššie, oranžové) */}
      <button onClick={startScan} style={{
        width: '100%', minHeight: 72, fontSize: TYPO.sizes.xl, fontWeight: TYPO.weights.bold,
        background: COLORS.brand, color: '#fff', border: 'none',
        borderRadius: RADIUS.lg, cursor: 'pointer', fontFamily: 'inherit',
        boxShadow: '0 4px 14px rgba(29,78,216,0.35)',
      }}>Skenuj QR (eKasa)</button>

      {/* Foto QR — fallback ak live skener nedeteguje */}
      <input ref={photoInputRef} type="file" accept="image/*" capture="environment"
        onChange={handlePhotoQr} style={{ display: 'none' }} />
      <button onClick={() => photoInputRef.current?.click()} style={{
        width: '100%', minHeight: 56, fontSize: TYPO.sizes.md, fontWeight: TYPO.weights.semibold,
        background: COLORS.nav, color: '#fff', border: 'none',
        borderRadius: RADIUS.md, cursor: 'pointer', fontFamily: 'inherit',
      }}>Odfoť QR (záložná možnosť)</button>

      {/* AI foto celého bločku (Claude Vision) — keď nie je QR / nečitateľný */}
      <input ref={aiPhotoRef} type="file" accept="image/*" capture="environment" onChange={handleAiPhoto} style={{ display: 'none' }} />
      <button onClick={() => aiPhotoRef.current?.click()} style={{
        width: '100%', minHeight: 64, fontSize: TYPO.sizes.lg, fontWeight: TYPO.weights.bold,
        background: '#7c3aed', color: '#fff', border: 'none', borderRadius: RADIUS.md, cursor: 'pointer', fontFamily: 'inherit',
        boxShadow: '0 3px 12px rgba(124,58,237,0.30)',
      }}>Odfoť bloček — AI prečíta</button>

      {/* Manuálne UID dokladu (keď QR nejde naskenovať) */}
      <div style={{ display: 'flex', gap: SPACE.sm }}>
        <input ref={uidInputRef} type="text" placeholder="alebo zadaj UID dokladu (O-…)"
          onKeyDown={e => { if (e.key === 'Enter') handleUidLookup() }}
          style={{ flex: 1, padding: '12px 14px', border: `1px solid ${COLORS.line}`, borderRadius: RADIUS.md, fontSize: TYPO.sizes.md, background: COLORS.bg, color: COLORS.text, boxSizing: 'border-box' }} />
        <button onClick={handleUidLookup} style={{ padding: '0 18px', minHeight: 48, fontWeight: TYPO.weights.semibold, background: COLORS.nav, color: '#fff', border: 'none', borderRadius: RADIUS.md, cursor: 'pointer', fontFamily: 'inherit' }}>Načítať</button>
      </div>

      {/* Ručné pridanie = sekundárne, menšie */}
      <button onClick={() => setPrefillForm({})} style={{
        width: '100%', minHeight: 56, fontSize: TYPO.sizes.md, fontWeight: TYPO.weights.semibold,
        background: 'transparent', color: COLORS.text, border: `1.5px solid ${COLORS.line}`,
        borderRadius: RADIUS.md, cursor: 'pointer', fontFamily: 'inherit',
      }}>+ Pridať ručne</button>
      </>}

      <input type="text" value={search} onChange={e => setSearch(e.target.value)}
        placeholder="Hľadať dodávateľ, kategória…"
        style={{
          width: '100%', padding: '12px 14px',
          border: `1px solid ${COLORS.line}`, borderRadius: RADIUS.md,
          fontSize: TYPO.sizes.md, background: COLORS.bg, color: COLORS.text,
          boxSizing: 'border-box',
        }} />

      {loading ? (
        <div style={{ padding: SPACE.xl, color: COLORS.muted, textAlign: 'center' }}>Načítavam…</div>
      ) : filtered.length === 0 ? (
        <div style={{ padding: SPACE.xl, color: COLORS.muted, textAlign: 'center',
          background: COLORS.bgSoft, borderRadius: RADIUS.md,
        }}>
          {items.length === 0 ? 'Žiadne bločky' : 'Nič nesedí filtru'}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: SPACE.sm }}>
          {filtered.map(it => (
            <div key={it.id} style={{
              background: COLORS.bg, border: `1px solid ${COLORS.line}`,
              borderLeft: `4px solid ${it.cost_center === 'zakazka' ? COLORS.brand : COLORS.nav}`,
              borderRadius: RADIUS.md, padding: SPACE.md,
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: SPACE.sm }}>
                <div style={{ fontWeight: TYPO.weights.bold, fontSize: TYPO.sizes.md, flex: 1 }}>
                  {it.supplier_name || '—'}
                </div>
                <div style={{ fontWeight: TYPO.weights.extra, color: COLORS.brand, fontSize: TYPO.sizes.lg }}>
                  {SK.eur(it.amount_total || 0)}
                </div>
              </div>
              <div style={{ display: 'flex', gap: SPACE.sm, alignItems: 'center', marginTop: 6, fontSize: TYPO.sizes.sm, color: COLORS.muted }}>
                <span style={{ fontFamily: TYPO.mono }}>{it.date_issued || '—'}</span>
                {Number(it.kind_confirmed || 0) === 1 ? <><span>{it.cost_center === 'zakazka' ? 'zákazka' : 'réžia'}</span>
                <span style={{ flex: 1 }}>· {it.cost_category || ''}</span></> : <span style={{ flex: 1 }}>čaká na Štefániu</span>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

// ── AddBlocekMobile (s podporou prefill) ──
function AddBlocekMobile({ onClose, onSaved, prefill = {}, ekasaItems = [] }) {
  const [form, setForm] = useState({
    supplier_name: prefill.supplier_name || '',
    supplier_ico:  prefill.supplier_ico  || '',
    supplier_dic:  prefill.supplier_dic  || '',
    date_issued:   prefill.date_issued   || todayLocal(),
    amount_total:  prefill.amount_total  || '',
    vat_rate:      prefill.vat_rate != null ? prefill.vat_rate : 23,
    cost_center:   prefill.cost_center   || 'rezia',
    cost_category: prefill.cost_category || '',
    project_id:    prefill.project_id    || '',
    payment_method: prefill.payment_method === 'hotovost' ? 'hotovost' : 'karta',   // V3 P1 (12.6): voľba, predvolená karta
    invoice_number: prefill.invoice_number || '',
    doc_file: prefill.doc_file || '',   // V3 P1: fotka bločku z AI
    notes: prefill.notes || '',
    ekasa_id: prefill.ekasa_id || '',   // ústava 135.3
    zaloha_id: '',                      // ústava 135.4 — z ktorej zálohy na nákup sa bloček vyúčtuje
    vyplatene: '',                      // koľko office naozaj vypláca (najviac suma bločku)
  })
  // otvorené zálohy na nákup a info, či som office (pokladňa sa hýbe len pri office — ústava 12.6)
  const [spracuje, setSpracuje] = useState(true)   // BLOCKY_BANKA_V3: kategóriu povinne vyberá len Štefánia (a Mirko); ostatní dávajú len návrh
  useEffect(() => { fetchAuth('/api/incoming-invoices?kto=1').then(r => r.ok ? r.json() : null).then(d => { if (d) setSpracuje(!!d.spracuje) }).catch(() => {}) }, [])
  const [zalohy, setZalohy] = useState([])
  const [somOffice, setSomOffice] = useState(false)
  useEffect(() => {
    fetchAuth('/api/zalohy-nakup').then(r => r.ok ? r.json() : null)
      .then(d => { if (d) { setZalohy(d.polozky || []); setSomOffice(!!d.office) } }).catch(() => {})
  }, [])
  // eKasa náležitosti (readonly displej, posielané do payloadu)
  const ekasaMeta = {
    vat_lines: Array.isArray(prefill.vat_lines) ? prefill.vat_lines : [],
    amount_excl_vat: prefill.amount_excl_vat != null ? Number(prefill.amount_excl_vat) : null,
  }
  const fromEkasa = !!prefill.invoice_number
  const [projects, setProjects] = useState([])
  const [busy, setBusy] = useState(false)
  const [dupWarn, setDupWarn] = useState(null)
  const toast = useToast()
  useEffect(() => {
    fetchAuth('/api/projects').then(r => r.ok ? r.json() : []).then(d => setProjects(Array.isArray(d) ? d : (d.rows || []))).catch(() => {})
  }, [])
  function set(k, v) { setForm(prev => ({ ...prev, [k]: v })) }
  const _s0 = useRef(null); if (_s0.current === null) _s0.current = JSON.stringify(form); useDirtyFlag(JSON.stringify(form) !== _s0.current, { key: 'blocek-mobil', discard: () => { onClose && onClose() } })   // straz_v4
  async function save(force = false) {
    if (!form.amount_total || (spracuje && !form.cost_category)) {
      toast.error(spracuje ? 'Vyplň sumu a kategóriu' : 'Vyplň sumu')
      return
    }
    if (form.cost_center === 'zakazka' && !form.project_id) {
      toast.error('Vyber zákazku')
      return
    }
    setBusy(true); setDupWarn(null)
    try {
      const amount = Number(form.amount_total) || 0
      const vatR = Number(form.vat_rate) || 0
      const base = vatR > 0 ? +(amount / (1 + vatR / 100)).toFixed(2) : amount
      const vat = +(amount - base).toFixed(2)
      // Ak máme eKasa vat_lines → použiť presné hodnoty namiesto re-computu
      const usedBase = ekasaMeta.vat_lines.length > 0 && ekasaMeta.amount_excl_vat != null
        ? ekasaMeta.amount_excl_vat
        : base
      const usedVat = ekasaMeta.vat_lines.length > 0
        ? ekasaMeta.vat_lines.reduce((s, v) => s + (Number(v.amount) || 0), 0)
        : vat

      const payload = {
        doc_type: 'blocok',
        is_receipt: 1,
        invoice_kind: 'nakladova',
        supplier_name: form.supplier_name.trim(),
        supplier_ico: form.supplier_ico.trim(),
        supplier_dic: form.supplier_dic.trim(),
        date_issued: form.date_issued,
        amount_excl_vat: usedBase,
        vat_rate: vatR,
        vat_amount: usedVat,
        vat_lines: ekasaMeta.vat_lines.length > 0 ? JSON.stringify(ekasaMeta.vat_lines) : '',
        amount_total: amount,
        cost_center: form.cost_center,
        cost_category: form.cost_category,
        project_id: form.cost_center === 'zakazka' ? form.project_id : '',
        payment_method: form.payment_method,   // V3 P1 (12.6): karta alebo hotovosť podľa voľby
        payment_date: form.date_issued,
        ...(form.doc_file ? { doc_file: form.doc_file } : {}),   // V3 P1: príloha dokladu
        status: 'paid',
        notes: form.notes,
        invoice_number: form.invoice_number,
        kind_confirmed: 1,
        ...(form.ekasa_id ? { ekasa_id: form.ekasa_id } : {}),
        ...(form.payment_method === 'hotovost' && form.zaloha_id ? { zaloha_id: form.zaloha_id } : {}),
        ...(form.payment_method === 'hotovost' && somOffice && form.vyplatene !== '' ? { vyplatene: Number(String(form.vyplatene).replace(',', '.')) } : {}),
        items: ekasaItems,
        ...(force ? { force: true } : {}),
      }
      const r = await fetchAuth('/api/incoming-invoices', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      })
      const d = await r.json()
      if (r.status === 403 && d.error === 'BLOCEK_NESMIES') { toast.error(d.message); return }
      if (r.status === 409 && d.error === 'BLOCEK_DUPLICITA') { toast.error(d.message); return }
      if (!r.ok) throw new Error(d.message || d.error || 'save failed')
      if (d.warning === 'duplicate') {
        setDupWarn({ existing: d.existing, reason: d.reason })
        return
      }
      toast.success(spracuje ? 'Bloček uložený' : 'Bloček uložený — čaká, kým ho Štefánia zaradí')
      if (d && d.duplicita) toast.error('Pozor: uložené ako možný duplikát dokladu ' + d.duplicita.existujuci + ' (' + d.duplicita.dovod + '). Skontroluj v Bločkoch.')
      if (onSaved) onSaved()
    } catch (e) {
      toast.error('Chyba: ' + e.message)
    } finally {
      setBusy(false)
    }
  }
  return (
    <div style={{ display: 'flex', flexDirection: 'column', padding: SPACE.md, gap: SPACE.md }}>
      <button onClick={onClose} style={{
        background: 'transparent', border: 'none', cursor: 'pointer',
        color: COLORS.nav, fontSize: TYPO.sizes.md, fontWeight: TYPO.weights.semibold,
        textAlign: 'left', padding: '8px 0', minHeight: 44,
      }}>‹ Späť na zoznam</button>
      <h2 style={{ margin: 0, fontSize: TYPO.sizes.lg, fontWeight: TYPO.weights.extra }}>
        {prefill.invoice_number ? `eKasa ${prefill.invoice_number.slice(0,12)}…` : 'Nový bloček'}
      </h2>

      {fromEkasa && (
        <div style={{
          background: 'rgba(34,197,94,0.06)', border: `1px solid ${COLORS.add}`,
          borderRadius: RADIUS.md, padding: SPACE.md,
        }}>
          <div style={{
            fontSize: TYPO.sizes.xs, fontWeight: TYPO.weights.bold, color: COLORS.add,
            textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: SPACE.sm,
          }}>Náležitosti z eKasa</div>

          <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '4px 12px', fontSize: TYPO.sizes.sm }}>
            <span style={{ color: COLORS.muted }}>Číslo bločka:</span>
            <span style={{ fontFamily: TYPO.mono, wordBreak: 'break-all' }}>{prefill.invoice_number || '—'}</span>
            <span style={{ color: COLORS.muted }}>Dodávateľ:</span>
            <span style={{ fontWeight: TYPO.weights.semibold }}>{form.supplier_name || '—'}</span>
            <span style={{ color: COLORS.muted }}>IČO:</span>
            <span style={{ fontFamily: TYPO.mono }}>{form.supplier_ico || '—'}</span>
            {form.supplier_dic && (<>
              <span style={{ color: COLORS.muted }}>DIČ:</span>
              <span style={{ fontFamily: TYPO.mono }}>{form.supplier_dic}</span>
            </>)}
            <span style={{ color: COLORS.muted }}>Dátum:</span>
            <span style={{ fontFamily: TYPO.mono }}>{form.date_issued || '—'}</span>
          </div>

          {ekasaMeta.vat_lines.length > 0 && (
            <div style={{ marginTop: SPACE.sm, paddingTop: SPACE.sm, borderTop: `1px solid ${COLORS.line}` }}>
              <div style={{
                fontSize: TYPO.sizes.xs, fontWeight: TYPO.weights.bold, color: COLORS.muted,
                textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 6,
              }}>Rozpis DPH ({ekasaMeta.vat_lines.length}× sadzba):</div>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: TYPO.sizes.sm }}>
                <thead>
                  <tr style={{ color: COLORS.muted, fontSize: TYPO.sizes.xs }}>
                    <th style={{ textAlign: 'left', padding: '4px 6px' }}>Sadzba</th>
                    <th style={{ textAlign: 'right', padding: '4px 6px' }}>Základ</th>
                    <th style={{ textAlign: 'right', padding: '4px 6px' }}>DPH</th>
                  </tr>
                </thead>
                <tbody>
                  {ekasaMeta.vat_lines.map((v, i) => (
                    <tr key={i} style={{ borderTop: `1px solid ${COLORS.line}` }}>
                      <td style={{ padding: '4px 6px', fontWeight: TYPO.weights.semibold }}>{Number(v.rate) || 0} %</td>
                      <td style={{ padding: '4px 6px', textAlign: 'right', fontFamily: TYPO.mono }}>{Number(v.base || 0).toFixed(2)} €</td>
                      <td style={{ padding: '4px 6px', textAlign: 'right', fontFamily: TYPO.mono, color: COLORS.brand }}>{Number(v.amount || 0).toFixed(2)} €</td>
                    </tr>
                  ))}
                  <tr style={{ borderTop: `2px solid ${COLORS.add}` }}>
                    <td style={{ padding: '4px 6px', fontWeight: TYPO.weights.bold }}>Spolu</td>
                    <td style={{ padding: '4px 6px', textAlign: 'right', fontFamily: TYPO.mono, fontWeight: TYPO.weights.bold }}>
                      {ekasaMeta.amount_excl_vat != null ? ekasaMeta.amount_excl_vat.toFixed(2) : '—'} €
                    </td>
                    <td style={{ padding: '4px 6px', textAlign: 'right', fontFamily: TYPO.mono, fontWeight: TYPO.weights.bold, color: COLORS.brand }}>
                      {ekasaMeta.vat_lines.reduce((s, v) => s + (Number(v.amount) || 0), 0).toFixed(2)} €
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {dupWarn && (
        <div style={{
          padding: SPACE.md, background: 'rgba(245,158,11,0.10)',
          border: `2px solid ${COLORS.warn || '#f59e0b'}`, borderRadius: RADIUS.md,
        }}>
          <div style={{ fontWeight: TYPO.weights.bold, marginBottom: 6 }}>Možný duplikát</div>
          <div style={{ fontSize: TYPO.sizes.sm, color: COLORS.muted, marginBottom: 10 }}>
            {dupWarn.reason || 'Server hovorí že bloček podobný tomuto už existuje.'}
          </div>
          <div style={{ display: 'flex', gap: SPACE.sm }}>
            <button onClick={() => save(true)} disabled={busy} style={{
              flex: 1, padding: '12px', background: COLORS.warn || '#f59e0b',
              color: '#fff', border: 'none', borderRadius: RADIUS.md,
              fontWeight: TYPO.weights.bold, cursor: 'pointer',
            }}>Pridať aj tak</button>
            <button onClick={onClose} style={{
              flex: 1, padding: '12px', background: 'transparent',
              color: COLORS.text, border: `1px solid ${COLORS.line}`, borderRadius: RADIUS.md,
              fontWeight: TYPO.weights.semibold, cursor: 'pointer',
            }}>Zrušiť</button>
          </div>
        </div>
      )}

      <FieldM label="Dátum *">
        <input type="date" value={form.date_issued} onChange={e => set('date_issued', e.target.value)} style={inpM} />
      </FieldM>
      <FieldM label="Dodávateľ">
        <input type="text" value={form.supplier_name} onChange={e => set('supplier_name', e.target.value)} placeholder="napr. Slovnaft" style={inpM} />
      </FieldM>
      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: SPACE.sm }}>
        <FieldM label="Suma celkom (€) *">
          <input type="number" step="0.01" min="0" value={form.amount_total} onChange={e => set('amount_total', e.target.value)} style={inpM} />
        </FieldM>
        <FieldM label="DPH %">
          <select value={form.vat_rate} onChange={e => set('vat_rate', e.target.value)} style={inpM}>
            <option value="0">0 %</option>
            <option value="5">5 %</option>
            <option value="10">10 %</option>
            <option value="19">19 %</option>
            <option value="20">20 %</option>
            <option value="23">23 %</option>
          </select>
        </FieldM>
      </div>

      <FieldM label="Platba *">
        <div style={{ display: 'flex', gap: SPACE.xs }}>
          {[
            { v: 'karta', l: 'Karta' },
            { v: 'hotovost', l: 'Hotovosť' },
          ].map(o => (
            <button key={o.v} type="button" aria-pressed={form.payment_method === o.v}
              onClick={() => set('payment_method', o.v)}
              style={{
                flex: 1, padding: '12px 16px',
                border: `2px solid ${form.payment_method === o.v ? COLORS.brand : COLORS.line}`,
                borderRadius: RADIUS.md,
                background: form.payment_method === o.v ? COLORS.brand : COLORS.bg,
                color: form.payment_method === o.v ? '#fff' : COLORS.text,
                fontWeight: TYPO.weights.bold, fontSize: TYPO.sizes.md,
                fontFamily: 'inherit', cursor: 'pointer', minHeight: 48,
              }}>{o.l}</button>
          ))}
        </div>
        {form.payment_method === 'hotovost' && somOffice && !form.zaloha_id && (
          <div style={{ marginTop: SPACE.sm }}>
            <div style={{ fontSize: TYPO.sizes.xs, color: COLORS.muted, marginBottom: 3 }}>Vyplácam z pokladne (€)</div>
            <input value={form.vyplatene} onChange={e => set('vyplatene', e.target.value)} inputMode="decimal"
              placeholder={form.amount_total ? 'celá suma ' + form.amount_total + ' €' : 'celá suma bločku'}
              style={{ width: '100%', padding: '12px 14px', border: `2px solid ${COLORS.line}`, borderRadius: RADIUS.md,
                fontFamily: 'inherit', fontSize: TYPO.sizes.md, background: COLORS.bg, color: COLORS.text, minHeight: 48, boxSizing: 'border-box' }} />
            <div style={{ fontSize: TYPO.sizes.xs, color: COLORS.muted, marginTop: 4 }}>
              Prázdne = vypláca sa celá suma bločku. Viac, než je na bločku, sa vyplatiť nedá.
            </div>
          </div>
        )}
        {form.payment_method === 'hotovost' && !somOffice && (
          <div style={{ fontSize: TYPO.sizes.xs, color: COLORS.muted, marginTop: 4 }}>
            Pokladňa sa nehýbe — hotovostný bloček mimo office sa iba zaradí (správna réžia firmy).
          </div>
        )}
        {form.payment_method === 'hotovost' && somOffice && zalohy.length > 0 && (
          <div style={{ marginTop: SPACE.sm }}>
            <div style={{ fontSize: TYPO.sizes.xs, color: COLORS.muted, marginBottom: 3 }}>Záloha na nákup (ak bola vydaná dopredu)</div>
            <select value={form.zaloha_id} onChange={e => set('zaloha_id', e.target.value)}
              style={{ width: '100%', padding: '12px 14px', border: `2px solid ${form.zaloha_id ? COLORS.brand : COLORS.line}`,
                borderRadius: RADIUS.md, fontFamily: 'inherit', fontSize: TYPO.sizes.md, background: COLORS.bg, color: COLORS.text, minHeight: 48 }}>
              <option value="">Bez zálohy — preplatí sa z pokladne</option>
              {zalohy.map(z => <option key={z.id} value={z.id}>{z.osoba} · {Number(z.suma).toFixed(2)} € · {z.ucel}</option>)}
            </select>
            {form.zaloha_id ? (
              <div style={{ fontSize: TYPO.sizes.xs, color: COLORS.muted, marginTop: 4 }}>
                Peniaze už z pokladne odišli pri vydaní zálohy — vyrovná sa iba rozdiel.
              </div>
            ) : null}
          </div>
        )}
        <div style={{ fontSize: TYPO.sizes.xs, color: COLORS.muted, marginTop: 4 }}>
          {form.payment_method === 'hotovost'
            ? 'Hotovosť = platila pokladňa (Office). Nad 400 € Matrix hotovosť nepustí.'
            : 'Karta = z pokladne sa nič neodráta (ústava 12.6).'}
        </div>
        {form.doc_file && (
          <div style={{ fontSize: TYPO.sizes.xs, color: COLORS.text, marginTop: 4, fontWeight: TYPO.weights.bold }}>
            Fotka bločku je priložená k dokladu.
          </div>
        )}
      </FieldM>

      <FieldM label="Stredisko *">
        <div style={{ display: 'flex', gap: SPACE.xs }}>
          {[
            { v: 'rezia', l: 'Réžia' },
            { v: 'zakazka', l: 'Zákazka' },
          ].map(o => (
            <button key={o.v} type="button"
              onClick={() => { set('cost_center', o.v); set('cost_category', '') }}
              style={{
                flex: 1, padding: '12px 16px',
                border: `2px solid ${form.cost_center === o.v ? COLORS.brand : COLORS.line}`,
                borderRadius: RADIUS.md,
                background: form.cost_center === o.v ? COLORS.brand : COLORS.bg,
                color: form.cost_center === o.v ? '#fff' : COLORS.text,
                fontWeight: TYPO.weights.bold, fontSize: TYPO.sizes.md,
                fontFamily: 'inherit', cursor: 'pointer', minHeight: 48,
              }}>{o.l}</button>
          ))}
        </div>
      </FieldM>

      <FieldM label={spracuje ? 'Kategória *' : 'Kategória (návrh — zaradí Štefánia)'}>
        <select value={form.cost_category} onChange={e => set('cost_category', e.target.value)} style={inpM}>
          <option value="">— vyber —</option>
          {(COST_CATS[form.cost_center] || []).map(c => <option key={c} value={c}>{c}</option>)}
        </select>
      </FieldM>

      {form.cost_center === 'zakazka' && (
        <FieldM label="Zákazka *">
          <select value={form.project_id} onChange={e => set('project_id', e.target.value)} style={inpM}>
            <option value="">— vyber zákazku —</option>
            {projects.map(p => <option key={p.id} value={p.id}>{p.id} · {p.name || ''}</option>)}
          </select>
        </FieldM>
      )}

      {ekasaItems.length > 0 && (
        <div style={{
          background: COLORS.bgSoft, border: `1px solid ${COLORS.line}`,
          borderRadius: RADIUS.md, padding: SPACE.md,
        }}>
          <div style={{
            fontSize: TYPO.sizes.xs, color: COLORS.muted, fontWeight: TYPO.weights.bold,
            textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: SPACE.sm,
          }}>
            Položky bločka ({ekasaItems.length})
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {ekasaItems.map((it, i) => {
              const name = it.name || it.title || it.popis || '—'
              const qty = it.qty != null ? it.qty : (it.quantity != null ? it.quantity : null)
              const unit = it.unit || it.mj || ''
              const priceTotal = Number(it.price_total != null ? it.price_total : (it.amount_total != null ? it.amount_total : (it.price || 0)))
              const priceUnit = it.price_unit != null ? Number(it.price_unit) : (it.unit_price != null ? Number(it.unit_price) : null)
              return (
                <div key={i} style={{
                  display: 'flex', alignItems: 'center', gap: SPACE.sm,
                  padding: '8px 10px', background: COLORS.bg, borderRadius: RADIUS.sm,
                  border: `1px solid ${COLORS.line}`,
                }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: TYPO.sizes.sm, color: COLORS.text, fontWeight: TYPO.weights.semibold }}>
                      {name}
                    </div>
                    {(qty != null || priceUnit != null) && (
                      <div style={{ fontSize: TYPO.sizes.xs, color: COLORS.muted, marginTop: 2 }}>
                        {qty != null ? `${qty}${unit ? ' ' + unit : ''}` : ''}
                        {qty != null && priceUnit != null ? ' · ' : ''}
                        {priceUnit != null ? `${priceUnit.toFixed(2)} €/ks` : ''}
                      </div>
                    )}
                  </div>
                  <div style={{
                    fontSize: TYPO.sizes.md, fontWeight: TYPO.weights.bold,
                    color: COLORS.brand, fontFamily: TYPO.mono, whiteSpace: 'nowrap',
                  }}>
                    {priceTotal.toFixed(2)} €
                  </div>
                </div>
              )
            })}
          </div>
          <div style={{
            marginTop: SPACE.sm, fontSize: TYPO.sizes.xs, color: COLORS.muted, textAlign: 'right',
          }}>
            Uložia sa spolu s bločkom.
          </div>
        </div>
      )}

      <FieldM label="Poznámka">
        <textarea value={form.notes} onChange={e => set('notes', e.target.value)} rows={2} style={{ ...inpM, resize: 'vertical' }} />
      </FieldM>

      <button onClick={() => save(false)} disabled={busy} style={bigBtn(COLORS.add, busy)}>
        {busy ? 'Ukladám…' : '✓ Uložiť bloček'}
      </button>
    </div>
  )
}

function FieldM({ label, children }) {
  return (
    <label style={{ display: 'block' }}>
      <div style={{ fontSize: TYPO.sizes.sm, fontWeight: TYPO.weights.semibold, color: COLORS.muted, marginBottom: 6 }}>{label}</div>
      {children}
    </label>
  )
}

const inpM = {
  width: '100%', padding: '12px 14px',
  border: `1.5px solid ${COLORS.line}`, borderRadius: RADIUS.md,
  fontSize: TYPO.sizes.md, background: COLORS.bg, color: COLORS.text,
  fontFamily: 'inherit', boxSizing: 'border-box', minHeight: 48,
}
const bigBtn = (color, disabled) => ({
  width: '100%', minHeight: 60, fontSize: TYPO.sizes.lg, fontWeight: TYPO.weights.bold,
  background: disabled ? COLORS.disabled : color, color: '#fff', border: 'none',
  borderRadius: RADIUS.lg, cursor: disabled ? 'not-allowed' : 'pointer', fontFamily: 'inherit',
  boxShadow: disabled ? 'none' : '0 4px 12px rgba(0,0,0,0.15)',
})
